import { isValidElement, ReactNode } from 'react';
import { ProductTrackingType } from '../../../../clients/quipli-service/product/product';
import {
  PurchaseOrderProductLineItem,
  UpsertPurchaseOrderProductInventoryUnits,
  UpsertPurchaseOrderProductStock,
} from '../../../../clients/quipli-service/purchase-order/purchase-order';
import Icon from '../../../../components/icons/icon';
import { Typography } from '../../../../components/typography/typography';

// Types for better readability
type ReceiveSummaryProps = {
  receivableProducts: PurchaseOrderProductLineItem[];
  units: UpsertPurchaseOrderProductInventoryUnits[];
  stocks: UpsertPurchaseOrderProductStock[];
};

type ReceiveSummaryDetail = {
  label: string;
  value: string | ReactNode;
};

type ReceiveSummaryGroup = {
  details: ReceiveSummaryDetail[];
};

type ReceiveSummaryCardProps = {
  lineItemId: string;
  name: string;
  total: string;
  groups: ReceiveSummaryGroup[];
};

export const ReceiveSummary = ({ receivableProducts, units, stocks }: ReceiveSummaryProps) => {
  return (
    <div className="flex flex-column gap-6">
      {receivableProducts.map((lineItem) => {
        // Get the groups for the product
        let groups: ReceiveSummaryGroup[] = [];

        if (lineItem.product.trackingType === ProductTrackingType.Quantity) {
          // If the product is tracking quantity, we need to get the stock for the product
          const stock = stocks.find((s) => s.purchaseOrderProductId === lineItem.id)!;
          groups = [
            {
              details: [
                { label: 'Sale Quantity', value: `${stock.saleQuantity}` },
                { label: 'Rental Quantity', value: `${stock.rentalQuantity}` },
              ],
            },
          ];
        } else if (lineItem.product.trackingType === ProductTrackingType.SerialUnits) {
          // If the product is tracking units, we need to get the units for the product
          const us = units.find((u) => u.purchaseOrderProductId === lineItem.id)!;
          groups = [
            ...us.units.map((u, idx) => ({
              details: [
                { label: 'Unit Number', value: u.unitNumber },
                { label: 'Serial Number', value: u.serialNumber },
                { label: 'QR Code', value: u.qrCode || '—' },
                { label: 'Machine Hours', value: u.hourReading || '—' },
                {
                  label: 'For Sale',
                  value: (
                    <Icon
                      icon={u.forSale ? 'Check' : 'Close'}
                      data-cy={`receive-summary-${lineItem.id}-group-${idx}-For Sale`}
                      data-is-sale={u.forSale}
                    />
                  ),
                },
                {
                  label: 'For Rent',
                  value: (
                    <Icon
                      icon={u.forRent ? 'Check' : 'Close'}
                      data-cy={`receive-summary-${lineItem.id}-group-${idx}-For Rent`}
                      data-is-rental={u.forRent}
                    />
                  ),
                },
              ],
            })),
          ];
        } else if (lineItem.product.trackingType === ProductTrackingType.NonInventory) {
          groups = [
            {
              details: [{ label: 'Non-Inventory', value: '—' }],
            },
          ];
        }
        return (
          <ReceiveSummaryCard
            key={`purchase-order-product-${lineItem.id}`}
            lineItemId={lineItem.id}
            name={lineItem.product.name}
            total={`${lineItem.quantity}`}
            groups={groups}
          />
        );
      })}
    </div>
  );
};

const ReceiveSummaryCard = ({ lineItemId, name, total, groups }: ReceiveSummaryCardProps) => {
  return (
    <div className="flex flex-column gap-3">
      <div className="flex gap-4 items-start justify-between mb-2">
        <div className="flex gap-2 flex-wrap">
          <Typography type="text-md" weight="medium" className="text-grey-700">
            Product:
          </Typography>
          <Typography type="text-md" className="text-quipli-black-500">
            {name}
          </Typography>
        </div>
        <div className="flex gap-2 shrink-0">
          <Typography type="text-md" weight="medium" className="text-grey-700">
            Total:
          </Typography>
          <Typography type="text-md" className="text-quipli-black-500">
            {total}
          </Typography>
        </div>
      </div>

      {groups.map((group, idx) => (
        <div key={`${lineItemId}-group-${idx}`} className="p-3 bg-grey-50 rounded-lg">
          <div className="grid grid-cols-2 gap-4">
            {group.details.map((detail) => (
              <div key={`${lineItemId}-group-${idx}-${detail.label}`} className="flex flex-column gap-1">
                <Typography type="text-xs" weight="medium" className="text-grey-500 uppercase tracking-wide">
                  {detail.label}
                </Typography>
                {isValidElement(detail.value) ? (
                  detail.value
                ) : (
                  <Typography
                    type="text-sm"
                    className="text-quipli-black-500"
                    dataCy={`receive-summary-${lineItemId}-group-${idx}-${detail.label}`}
                  >
                    {detail.value}
                  </Typography>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
